import React, { useMemo, useState } from 'react';
import { modalStyles as styles } from './modalStyles';
import { ACCESSORY_FIELDS } from '../rmaFieldUtils';
import { locationLabel, locationAddress } from '../utils/rmaLocation';

// Only a fallback for cases assigned before the address was pinned to the case
// (issue #728) -- otherwise the snapshot on the case wins.
const LEGACY_LOCATIONS = {
  Haugesund: 'Sorhauggata 128, N-5527 Haugesund, Norway',
  Copenhagen: 'C/o Aalborg University, A. C. Meyers Vaenge 15, 2450 Copenhagen SV, Denmark'
};

const initialShipmentState = {
  trackingNumber: '',
  carrier: '',
  comment: ''
};

export default function CustomerRmaModalAssigned({ rma, onClose, trackingActions, onShipmentSaved }) {
  const expectedReturn = rma.expectedReturn || { items: [], accessories: {} };
  const expectedItems = Array.isArray(expectedReturn.items) ? expectedReturn.items : [];
  const accessories = expectedReturn.accessories || {};
  const accessoryEntries = ACCESSORY_FIELDS.filter(({ key }) => Number(accessories[key] || 0) > 0)
    .map(({ key, label }) => ({
      label,
      qty: Number(accessories[key] || 0)
    }));

  const customerShipment = rma.customerShipment && rma.customerShipment.trackingNumber ? rma.customerShipment : null;
  const [shipmentForm, setShipmentForm] = useState(initialShipmentState);
  const [shipmentError, setShipmentError] = useState('');
  const [shipmentStatus, setShipmentStatus] = useState('');
  const [submittingShipment, setSubmittingShipment] = useState(false);
  const canSubmitShipment = Boolean(trackingActions?.canSubmit && !customerShipment);
  const shipTo = useMemo(
    () => locationAddress(rma, LEGACY_LOCATIONS[rma.assignedLocation] || ''),
    [rma]
  );

  const handleShipmentChange = event => {
    const { name, value } = event.target;
    setShipmentForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitShipment = async event => {
    event.preventDefault();
    if (!canSubmitShipment) {
      return;
    }
    const trackingNumber = shipmentForm.trackingNumber.trim();
    if (trackingNumber.length < 6) {
      setShipmentError('Enter a valid tracking number.');
      return;
    }
    setShipmentError('');
    setShipmentStatus('');
    setSubmittingShipment(true);
    try {
      if (!trackingActions?.submit) {
        throw new Error('Tracking submission is not configured.');
      }
      const payload = await trackingActions.submit({
        trackingNumber,
        carrier: shipmentForm.carrier.trim(),
        comment: shipmentForm.comment.trim()
      });
      setShipmentForm(initialShipmentState);
      setShipmentStatus('Tracking submitted.');
      if (typeof onShipmentSaved === 'function') {
        onShipmentSaved(payload);
      }
    } catch (err) {
      setShipmentError(err.message || 'Failed to submit tracking.');
    } finally {
      setSubmittingShipment(false);
    }
  };

  const formatShipmentDate = value => {
    if (!value) return '';
    try {
      return new Date(value).toLocaleString();
    } catch (err) {
      return value;
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <h2>Ship Device</h2>
        <p><strong>Assigned Location:</strong> {locationLabel(rma)}</p>
        {shipTo && <p><strong>Address:</strong> {shipTo}</p>}
        <p><strong>Serial Number:</strong> {rma.serialNumber}</p>
        <div style={{ marginTop: '1rem' }}>
          <h3 style={{ margin: '0 0 0.5rem' }}>Include these items</h3>
          {expectedItems.length ? (
            <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
              {expectedItems.map((item, index) => (
                <li key={`${item.name || 'item'}-${index}`}>
                  <strong>{item.name || 'Unnamed item'}</strong>
                  {typeof item.quantity === 'number' && item.quantity > 0 && ` - ${item.quantity}`}
                  {Array.isArray(item.serials) && item.serials.length > 0 && (
                    <div style={{ fontSize: '0.85rem' }}>
                      Serials: {item.serials.join(', ')}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          ) : null}
          {accessoryEntries.length ? (
            <div style={{ marginTop: expectedItems.length ? '0.75rem' : '0.25rem' }}>
              <strong>Accessories:</strong>
              <ul style={{ margin: '0.35rem 0 0', paddingLeft: '1.25rem' }}>
                {accessoryEntries.map(entry => (
                  <li key={entry.label}>
                    {entry.label} - {entry.qty}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {!expectedItems.length && !accessoryEntries.length && (
            <p>No accessories or extra parts were specified.</p>
          )}
          {expectedReturn.notes && (
            <p style={{ fontSize: '0.85rem' }}>Notes: {expectedReturn.notes}</p>
          )}
        </div>

        <div style={{ marginTop: '1.25rem' }}>
          <h3 style={{ margin: '0 0 0.5rem' }}>Customer shipment</h3>
          {customerShipment ? (
            <div>
              <p style={{ margin: '0 0 0.35rem' }}>
                <strong>Tracking:</strong> {customerShipment.trackingNumber}
              </p>
              {customerShipment.carrier && (
                <p style={{ margin: '0 0 0.35rem' }}>
                  <strong>Carrier:</strong> {customerShipment.carrier}
                </p>
              )}
              {customerShipment.comment && (
                <p style={{ margin: '0 0 0.35rem' }}>
                  <strong>Comment:</strong> {customerShipment.comment}
                </p>
              )}
              {customerShipment.shippedAt && (
                <p style={{ margin: 0, fontSize: '0.85rem' }}>
                  Submitted {formatShipmentDate(customerShipment.shippedAt)}
                </p>
              )}
            </div>
          ) : canSubmitShipment ? (
            <form
              onSubmit={handleSubmitShipment}
              style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
            >
              <input
                name="trackingNumber"
                placeholder="Tracking number"
                value={shipmentForm.trackingNumber}
                onChange={handleShipmentChange}
                style={styles.input}
                required
              />
              <input
                name="carrier"
                placeholder="Carrier (optional)"
                value={shipmentForm.carrier}
                onChange={handleShipmentChange}
                style={styles.input}
              />
              <textarea
                name="comment"
                placeholder="Comment (optional)"
                value={shipmentForm.comment}
                onChange={handleShipmentChange}
                style={{ ...styles.input, minHeight: '4rem' }}
              />
              {shipmentError && <p style={{ color: 'salmon', margin: 0 }}>{shipmentError}</p>}
              {shipmentStatus && <p style={{ color: 'lightgreen', margin: 0 }}>{shipmentStatus}</p>}
              <button type="submit" style={styles.button} disabled={submittingShipment}>
                {submittingShipment ? 'Submitting...' : 'Submit tracking'}
              </button>
            </form>
          ) : (
            <p style={{ margin: 0, fontSize: '0.9rem' }}>
              Tracking details will appear here once submitted.
            </p>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
          <button onClick={onClose} style={{ ...styles.buttonAlt }}>Close</button>
        </div>
      </div>
    </div>
  );
}
